import React from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  List,
  Box,
  ProgressBar,
  Grid,
  Icon,
  Button,
  ButtonGroup,
  Tooltip
} from '@shopify/polaris';
import {
  StarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckIcon,
  AlertTriangleIcon,
  InfoIcon,
  CalendarIcon,
  ClockIcon,
  TargetIcon,
  PhoneIcon,
  ChatIcon
} from '@shopify/polaris-icons';
import WidgetRenderer from './WidgetRenderer';

interface UiMetadata {
  title: string;
  layout: string;
  summary: string;
  widget_type?: string;
  components: Array<{
    path: string;
    type: string;
    chart?: string;
    label?: string;
    scale?: number[] | string;
    thresholds?: {
      good: number;
      warn: number;
    };
    items?: string[];
    badge_map?: Record<string, string>;
  }>;
}

interface PromptResult {
  id: number;
  tenant_id: number;
  call_log_id: number;
  prompt_model_id: number;
  response_json: any;
  metadata: {
    ui_metadata: UiMetadata;
  };
  created_at: string;
  updated_at: string;
  error_message?: string;
  prompt_name?: string;
}

interface EnhancedCallDashboardProps {
  results: PromptResult[];
}

const EnhancedCallDashboard: React.FC<EnhancedCallDashboardProps> = ({ results }) => {
  const formatKey = (key: any): string => {
    if (typeof key !== 'string') {
      return String(key);
    }
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  const getBadgeTone = (value: any, badgeMap?: Record<string, string>): 'success' | 'warning' | 'critical' | 'info' => {
    const stringValue = String(value);
    
    if (badgeMap && badgeMap[stringValue]) {
      const tone = badgeMap[stringValue];
      if (tone === 'positive') return 'success';
      if (tone === 'negative') return 'critical';
      if (tone === 'neutral') return 'info';
    }
    
    const lower = stringValue.toLowerCase();
    if (lower.includes('high') || lower.includes('excellent') || lower.includes('good') || lower.includes('yes')) return 'success';
    if (lower.includes('medium') || lower.includes('average') || lower.includes('fair')) return 'warning';
    if (lower.includes('low') || lower.includes('poor') || lower.includes('no')) return 'critical';
    return 'info';
  };

  const getScoreColor = (percentage: string | number): string => {
    const num = typeof percentage === 'string' ? parseInt(percentage.replace('%', '')) : percentage;
    if (num >= 80) return '#00a047'; // Green
    if (num >= 60) return '#ffc453'; // Yellow
    return '#d72c0d'; // Red
  };

  const getScoreIcon = (percentage: string | number) => {
    const num = typeof percentage === 'string' ? parseInt(percentage.replace('%', '')) : percentage;
    if (num >= 80) return ArrowUpIcon;
    if (num >= 60) return InfoIcon;
    return ArrowDownIcon;
  };

  const getValueFromPath = (obj: any, path: string) => {
    if (path.startsWith('$.')) {
      const key = path.substring(2);
      return obj[key];
    }
    
    // Handle nested paths like "sentiment_analysis.analysis"
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        return undefined;
      }
    }
    return value;
  };

  const renderGaugeChart = (data: any, label: string) => {
    if (typeof data === 'object' && data.percentage) {
      const percentage = parseInt(data.percentage.replace('%', ''));
      const color = getScoreColor(percentage);
      
      return (
        <div style={{ 
          height: '100%',
          backgroundColor: '#f6f6f7',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e1e3e5',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <BlockStack gap="300" align="center">
            <Text variant="headingSm" as="h4" alignment="center">{label}</Text>
            
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: '120px'
            }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: `conic-gradient(${color} 0deg ${(percentage / 100) * 360}deg, #e5e7eb ${(percentage / 100) * 360}deg 360deg)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ 
                    color: color,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    {percentage}%
                  </span>
                </div>
              </div>
            </div>
            
            {(data as any).details && (
              <Text variant="bodySm" tone="subdued" as="p" alignment="center">
                {(data as any).details}
              </Text>
            )}
          </BlockStack>
        </div>
      );
    }
    return null;
  };

  const renderDonutChart = (data: any, label: string) => {
    if (typeof data === 'object') {
      const entries = Object.entries(data).filter(([key, value]: [string, any]) => 
        value && typeof value === 'object' && value.percentage
      );
      
      if (entries.length === 0) return null;
      
      const total = entries.reduce((sum, [key, value]: [string, any]) => 
        sum + parseInt((value as any).percentage.replace('%', '')), 0
      );
      const average = Math.round(total / entries.length);
      
      return (
        <BlockStack gap="300">
          <Text variant="headingSm" as="h4">{label}</Text>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ 
              width: '150px', 
              height: '150px', 
              borderRadius: '50%', 
              background: `conic-gradient(
                #00a047 0deg ${(parseInt((entries[0][1] as any).percentage.replace('%', '')) / 100) * 360}deg,
                #ffc453 ${(parseInt((entries[0][1] as any).percentage.replace('%', '')) / 100) * 360}deg ${((parseInt((entries[0][1] as any).percentage.replace('%', '')) + parseInt((entries[1]?.[1] as any)?.percentage.replace('%', '') || '0')) / 100 * 360)}deg,
                #d72c0d ${((parseInt((entries[0][1] as any).percentage.replace('%', '')) + parseInt((entries[1]?.[1] as any)?.percentage.replace('%', '') || '0')) / 100 * 360)}deg 360deg
              )`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <span style={{ 
                  color: getScoreColor(average),
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}>
                  {average}%
                </span>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  Average
                </span>
              </div>
            </div>
          </div>
          <BlockStack gap="200">
            {entries.map(([key, value]: [string, any], index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: getScoreColor((value as any).percentage),
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                  <span style={{ fontWeight: '500' }}>{formatKey(key)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon source={getScoreIcon((value as any).percentage)} />
                  <span style={{ 
                    color: getScoreColor((value as any).percentage),
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}>
                    {(value as any).percentage}
                  </span>
                </div>
              </div>
            ))}
          </BlockStack>
        </BlockStack>
      );
    }
    return null;
  };

  const renderScoreTable = (data: any, label: string, scale: number[] | string, thresholds: { good: number; warn: number }) => {
    if (typeof data === 'object') {
      const entries = Object.entries(data).filter(([key, value]: [string, any]) => 
        value && typeof value === 'object' && (value.score || value.percentage)
      );
      
      if (entries.length === 0) return null;
      
      return (
        <BlockStack gap="300">
          <Text variant="headingSm" as="h4">{label}</Text>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f6f6f7' }}>
                  <th style={{ textAlign: 'left', padding: '12px', borderBottom: '2px solid #e1e3e5', fontWeight: '600' }}>
                    Category
                  </th>
                  <th style={{ textAlign: 'center', padding: '12px', borderBottom: '2px solid #e1e3e5', fontWeight: '600' }}>
                    Score
                  </th>
                  <th style={{ textAlign: 'center', padding: '12px', borderBottom: '2px solid #e1e3e5', fontWeight: '600' }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([key, value]: [string, any], index) => {
                  const score = parseInt(value.score || value.percentage?.replace('%', '') || '0');
                  const status = score >= thresholds.good ? 'Excellent' : score >= thresholds.warn ? 'Good' : 'Needs Improvement';
                  const statusTone = score >= thresholds.good ? 'success' : score >= thresholds.warn ? 'warning' : 'critical';
                  
                  return (
                    <tr key={index} style={{ 
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                      borderBottom: '1px solid #f1f2f3'
                    }}>
                      <td style={{ padding: '12px' }}>
                        <InlineStack gap="200" align="start">
                          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Icon source={getScoreIcon(score)} />
                          </div>
                          <Text variant="bodySm" as="span">{formatKey(key)}</Text>
                        </InlineStack>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: getScoreColor(score), fontWeight: '600' }}>
                            {value.score || value.percentage || '0'}
                          </span>
                          <ProgressBar 
                            progress={score} 
                            size="small"
                          />
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <Badge tone={statusTone}>{status}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </BlockStack>
      );
    }
    return null;
  };

  const renderActionableItems = (items: any[], label: string) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    
    // Sort items by priority: high -> medium -> low -> others
    const sortedItems = [...items].sort((a, b) => {
      const priorityOrder = { high: 1, medium: 2, low: 3 };
      const aPriority = (a.priority || '').toLowerCase();
      const bPriority = (b.priority || '').toLowerCase();
      
      const aOrder = priorityOrder[aPriority as keyof typeof priorityOrder] || 4;
      const bOrder = priorityOrder[bPriority as keyof typeof priorityOrder] || 4;
      
      return aOrder - bOrder;
    });
    
    return (
      <BlockStack gap="300">
        <InlineStack align="space-between">
          <Text variant="headingSm" as="h4">{label}</Text>
          <Badge tone="info">{`${items.length} items`}</Badge>
        </InlineStack>
        <BlockStack gap="300">
          {sortedItems.map((item: any, index: number) => (
            <Box key={index} padding="300" background="bg-surface-secondary">
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <InlineStack gap="200" align="center">
                    <Icon source={item.type === 'site_visit' ? TargetIcon : PhoneIcon} />
                    <Text variant="headingSm" as="h5">
                      {item.type ? formatKey(item.type).toUpperCase() : 'ACTION ITEM'}
                    </Text>
                  </InlineStack>
                  <Badge tone={item.priority === 'high' ? 'critical' : item.priority === 'medium' ? 'warning' : 'info'}>
                    {item.priority ? item.priority.toUpperCase() : 'MEDIUM'}
                  </Badge>
                </InlineStack>
                
                <Text variant="bodySm" as="p">
                  {item.description || item.text || 'No description available'}
                </Text>
                
                {item.due_date && (
                  <InlineStack gap="200" align="start">
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Icon source={CalendarIcon} />
                    </div>
                    <Text variant="bodySm" tone="subdued" as="span">
                      Due: {new Date(item.due_date).toLocaleDateString()}
                    </Text>
                  </InlineStack>
                )}
              </BlockStack>
            </Box>
          ))}
        </BlockStack>
      </BlockStack>
    );
  };

  const renderSimpleList = (items: any[], label: string) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    
    return (
      <BlockStack gap="300">
        <Text variant="headingSm" as="h4">{label}</Text>
        <List type="bullet">
          {items.map((item: any, index: number) => (
            <List.Item key={index}>
              <Text variant="bodySm" as="span">
                {typeof item === 'object' ? JSON.stringify(item) : String(item)}
              </Text>
            </List.Item>
          ))}
        </List>
      </BlockStack>
    );
  };

  const renderTags = (tags: any[], label: string, badgeMap?: Record<string, string>) => {
    if (!Array.isArray(tags) || tags.length === 0) return null;
    
    return (
      <BlockStack gap="300">
        <Text variant="headingSm" as="h4">{label}</Text>
        <InlineStack gap="200" wrap>
          {tags.map((tag: any, index: number) => {
            if (typeof tag === 'object' && tag.tag && tag.value) {
              return (
                <Badge key={index} tone={getBadgeTone(tag.value, badgeMap)}>
                  {`${formatKey(tag.tag)}: ${tag.value}`}
                </Badge>
              );
            } else if (typeof tag === 'string') {
              return (
                <Badge key={index} tone={getBadgeTone(tag, badgeMap)}>
                  {tag}
                </Badge>
              );
            } else if (typeof tag === 'object' && tag.__ui && tag.percentage) {
              // Handle objects with __ui and percentage properties
              return (
                <Badge key={index} tone={getBadgeTone(tag.percentage, badgeMap)}>
                  {`${formatKey(tag.__ui)}: ${tag.percentage}`}
                </Badge>
              );
            } else if (typeof tag === 'object') {
              // Handle other object types by converting to string
              return (
                <Badge key={index} tone="info">
                  {JSON.stringify(tag)}
                </Badge>
              );
            }
            return null;
          })}
        </InlineStack>
      </BlockStack>
    );
  };

  const renderMetric = (value: any, label: string, unit: string = '') => {
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    
    return (
      <BlockStack gap="200" align="center">
        <Text variant="headingSm" as="h4">{label}</Text>
        <div style={{ textAlign: 'center' }}>
          <span style={{ 
            color: getScoreColor(numValue),
            fontSize: '2rem',
            fontWeight: 'bold'
          }}>
            {value}{unit}
          </span>
        </div>
        <Badge tone={numValue >= 7 ? 'success' : numValue >= 5 ? 'warning' : 'critical'}>
          {numValue >= 7 ? 'Good' : numValue >= 5 ? 'Average' : 'Needs Improvement'}
        </Badge>
      </BlockStack>
    );
  };

  const renderLongText = (text: string, label: string) => {
    return (
      <BlockStack gap="300">
        <Text variant="headingSm" as="h4">{label}</Text>
        <Box padding="300" background="bg-surface-secondary">
          <div style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
            <Text variant="bodySm" as="p">{text}</Text>
          </div>
        </Box>
      </BlockStack>
    );
  };

  const renderComponent = (component: any, responseJson: any) => {
    const { path, type, chart, label, scale, thresholds, items, badge_map } = component;
    const value = getValueFromPath(responseJson, path);
    
    if (value === undefined || value === null) return null;
    
    // Safety check: if value is an object with __ui and percentage, handle it specially
    if (typeof value === 'object' && value.__ui && value.percentage) {
      return renderTags([value], label || 'Metric', badge_map);
    }
    
    switch (type) {
      case 'chart':
        if (chart === 'gauge') {
          return renderGaugeChart(value, label || 'Gauge Chart');
        }
        if (chart === 'donut') {
          return renderDonutChart(value, label || 'Donut Chart');
        }
        return null;
        
      case 'score_table':
        return renderScoreTable(value, label || 'Score Table', scale || [0, 10], thresholds || { good: 7, warn: 5 });
        
      case 'list':
        if (Array.isArray(value)) {
          // Check if this is actually actionable items (has type, description, etc.)
          const hasActionableStructure = value.some(item => 
            item && typeof item === 'object' && 
            (item.type || item.description || item.due_date || item.priority)
          );
          
          if (hasActionableStructure) {
            return renderActionableItems(value, label || 'Action Items');
          } else {
            // Skip rendering as list if this looks like tag data (has tag/value structure)
            const hasTagStructure = value.some(item => 
              item && typeof item === 'object' && 
              (item.tag && item.value)
            );
            
            if (hasTagStructure) {
              // This is tag data, don't render as list
              return null;
            }
            
            // For other arrays, render as a simple list
            return renderSimpleList(value, label || 'List');
          }
        }
        return null;
        
      case 'badges':
        if (Array.isArray(value)) {
          return renderTags(value, label || 'Tags', badge_map);
        }
        return null;
        
      case 'metric':
        return renderMetric(value, label || 'Metric', component.unit || '');
        
      case 'long_text':
        return renderLongText(String(value), label || 'Details');
        
      default:
        return null;
    }
  };

  const renderSimpleTable = (data: any, title: string, promptName?: string) => {
    if (!data || typeof data !== 'object') return null;
    
    const entries = Object.entries(data);
    if (entries.length === 0) return null;
    
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {entries.map(([key, value]: [string, any], index) => (
              <tr key={index} style={{ 
                backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                borderBottom: '1px solid #f1f2f3'
              }}>
                <td style={{ 
                  padding: '12px', 
                  fontWeight: '600', 
                  width: '30%',
                  borderRight: '1px solid #f1f2f3'
                }}>
                  {formatKey(key)}
                </td>
                <td style={{ padding: '12px' }}>
                  <Text variant="bodySm" as="span">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </Text>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderResult = (result: PromptResult) => {
    const { response_json, metadata } = result;
    const ui = metadata?.ui_metadata;
    
    // Get the card title from UI metadata or prompt name
    const cardTitle = ui?.title || (result.prompt_name ? formatKey(result.prompt_name) : 'Analysis Result');
    
    if (result.error_message) {
      return (
        <Card padding="500" background="bg-surface-critical">
          <BlockStack gap="400">
            <Text variant="headingMd" as="h3">{cardTitle}</Text>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text variant="headingSm" as="h4">Error</Text>
                <Badge tone="critical">Failed</Badge>
              </InlineStack>
              <Text variant="bodySm" tone="critical" as="p">
                {result.error_message}
              </Text>
            </BlockStack>
          </BlockStack>
        </Card>
      );
    }

    // Normalize items for WidgetRenderer - handle different response shapes
    let items: any[] = [];
    let widgetType = ui?.widget_type;

    if (response_json) {
      if (Array.isArray(response_json)) {
        items = response_json;
        if (!widgetType && items.length > 0) {
          const first = items[0];
          if (typeof first === 'object' && first !== null) {
            const keys = Object.keys(first).map(k => k.toLowerCase());
            if (keys.includes('tag') && keys.includes('value')) widgetType = 'key_value';
            else if (keys.includes('key') && keys.includes('value')) widgetType = 'summary_card';
          }
        }
      } else if (response_json.items && Array.isArray(response_json.items)) {
        items = response_json.items;
      } else if (typeof response_json === 'object' && !Array.isArray(response_json)) {
        items = Object.entries(response_json).map(([key, value]) => {
          let displayValue: any = value;
          if (typeof value === 'object' && value !== null) {
            if ('included' in value && 'rating' in value) {
              displayValue = `${(value as any).included ? 'Included' : 'Not included'} (${(value as any).rating}/5)`;
            } else {
              displayValue = JSON.stringify(value);
            }
          }
          return { key: formatKey(key), value: displayValue };
        });
      }
    }

    if (items.length > 0) {
      return (
        <WidgetRenderer
          widgetType={widgetType || 'key_value'}
          items={items}
          title={cardTitle}
        />
      );
    }

    // Fallback to old rendering for backward compatibility
    if (!ui) {
      return (
        <Card padding="500">
          <BlockStack gap="400">
            <Text variant="headingMd" as="h3">{cardTitle}</Text>
            {renderSimpleTable(response_json, '', result.prompt_name)}
          </BlockStack>
        </Card>
      );
    }

    return (
      <Card padding="500">
        <BlockStack gap="400">
          {/* Header */}
          <Text variant="headingMd" as="h3">{cardTitle}</Text>

          {/* Summary */}
          {ui.summary && (
            <Text variant="bodyMd" tone="subdued" as="p">{ui.summary}</Text>
          )}

          {/* Components */}
          {ui.components && ui.components.length > 0 ? (
            <BlockStack gap="400">
              {(() => {
                // Group gauge charts together for grid layout
                const gaugeComponents = ui.components.filter(comp => 
                  comp.type === 'chart' && comp.chart === 'gauge'
                );
                const otherComponents = ui.components.filter(comp => 
                  !(comp.type === 'chart' && comp.chart === 'gauge')
                );
                
                const renderedComponents = [];
                
                // Render gauge charts in a grid if there are any
                if (gaugeComponents.length > 0) {
                  renderedComponents.push(
                    <div key="gauge-grid" style={{ 
                      marginBottom: '16px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '16px',
                      alignItems: 'stretch'
                    }}>
                      {gaugeComponents.map((component, index) => {
                        const renderedComponent = renderComponent(component, response_json);
                        if (!renderedComponent || typeof renderedComponent !== 'object' || !renderedComponent.type) return null;
                        return (
                          <div key={`gauge-${index}`} style={{ height: '100%' }}>
                            {renderedComponent}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                
                // Render other components normally
                otherComponents.forEach((component, index) => {
                  const renderedComponent = renderComponent(component, response_json);
                  if (!renderedComponent || typeof renderedComponent !== 'object' || !renderedComponent.type) return;
                  
                  renderedComponents.push(
                    <div key={`other-${index}`}>
                      {renderedComponent}
                    </div>
                  );
                });
                
                return renderedComponents;
              })()}
            </BlockStack>
          ) : (
            <Box padding="400" background="bg-surface-secondary">
              <Text variant="bodyMd" as="p">
                No specific components defined for this analysis.
              </Text>
            </Box>
          )}
        </BlockStack>
      </Card>
    );
  };

  return (
    <BlockStack gap="600">
      {results.map((result, index) => (
        <div key={result.id}>
          {renderResult(result)}
        </div>
      ))}
    </BlockStack>
  );
};

export default EnhancedCallDashboard;
